import React, { useContext, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import SelectField from './SelectField';
import TextField from './TextField';
import { COMMISSION_EVENT_TYPES, USER_ROLES } from '../constants';
import { formatNumber, parseEuroNumber } from '../utils/helper';
import EuroNumberInput from './EuroNumberField';
import { UserContext } from '../context/UserContext';
import CompanyLogo from './CompanyLogo';

const buildSchema = (requireProduct) =>
  z.object({
    client: z.string().min(1, 'Client is required'),
    product: requireProduct ? z.string().min(1, 'Product is required') : z.string().optional(),
    prospect: z.string().min(1, 'Prospect is required'),
    event: z.string().min(1, 'Event Type is required'),
    otherEvent: z.string(),
    commission: z.number().min(1, 'Amount is required'),
    cents: z.string(),
    notes: z.string(),
  });

const SubmitCommissionDialog = ({
  openDialog,
  setOpenDialog,
  submitConfirmOpen,
  setSubmitConfirmOpen,
  onSubmitCommission,
  companies = [],
  products = [],
  currentMonth,
  currentYear,
  defaultValues = {},
  isSubmitting = false,
}) => {
  const { user } = useContext(UserContext);

  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isEdit = Boolean(defaultValues?._id);
  const schema = useMemo(() => buildSchema(!isEdit), [isEdit]);

  const methods = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      leadAgent: '',
      client: '',
      product: '',
      prospect: '',
      event: '',
      commission: '',
      cents: '',
      otherEvent: '',
      notes: '',
    },
  });

  const {
    handleSubmit,
    watch,
    formState: { isValid, isDirty, touchedFields },
    reset,
    setValue,
  } = methods;

  useEffect(() => {
    methods.clearErrors();
    methods.control._options.resolver = zodResolver(schema);
  }, [schema, methods]);

  const selectedClientId = watch('client');

  const productsForClient = useMemo(() => {
    if (!selectedClientId) return [];
    const company = companies?.find((c) => String(c?.client?._id) === String(selectedClientId));
    if (!company?._id) return [];
    return (products || []).filter((p) => String(p.company?._id || p.company) === String(company._id));
  }, [companies, products, selectedClientId]);

  useEffect(() => {
    if (openDialog) {
      const initialClient = defaultValues.client?._id || '';
      const initialProduct = defaultValues.product?._id || defaultValues.product || '';
      reset({
        leadAgent: `${defaultValues.leadAgent?.firstName || ''} ${defaultValues.leadAgent?.lastName || ''}`,
        client: initialClient,
        product: initialProduct,
        prospect: defaultValues.prospect || '',
        event: defaultValues.event || '',
        commission: defaultValues.commission ? defaultValues.commission : '',
        cents: defaultValues.cents ? String(defaultValues.cents) : '',
        otherEvent: defaultValues.otherEvent || '',
        notes: defaultValues.notes || '',
      });
    }
    // eslint-disable-next-line
  }, [openDialog, reset, defaultValues?._id]);

  useEffect(() => {
    if (!openDialog || isEdit) return;
    if (productsForClient.length === 1) {
      setValue('product', productsForClient[0]._id, { shouldValidate: true, shouldDirty: true });
    } else if (productsForClient.length === 0) {
      setValue('product', '', { shouldValidate: true });
    } else {
      const currentProduct = watch('product');
      const stillValid = productsForClient.some((p) => String(p._id) === String(currentProduct));
      if (!stillValid) {
        setValue('product', '', { shouldValidate: true });
      }
    }
    // eslint-disable-next-line
  }, [productsForClient, openDialog, isEdit, setValue]);

  const companiesList = useMemo(
    () =>
      companies.map((item) => ({
        value: item.client?._id || '',
        label: (
          <Box display='flex' alignItems='center' gap={1}>
            <CompanyLogo url={item.companyLogo} />
            <Typography>{item.companyName}</Typography>
          </Box>
        ),
      })),
    [companies],
  );

  const productsList = useMemo(
    () =>
      productsForClient.map((item) => ({
        value: item._id,
        label: item.productName,
      })),
    [productsForClient],
  );

  const eventTypesList = useMemo(() => COMMISSION_EVENT_TYPES.map((item) => ({ value: item, label: item })), []);

  const selectedEventType = watch('event');
  const otherEvent = watch('otherEvent');
  const selectedClient = companies?.find((c) => String(c?.client?._id) === String(selectedClientId));
  const selectedClientName = `${selectedClient?.companyName || ''}` || 'the client';

  const handleDecimalChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setValue('cents', value, { shouldDirty: true, shouldValidate: true });
  };

  const commission = watch('commission');
  const cents = watch('cents');

  const convertToUSDFormat = () => {
    if (!commission && !cents) return '';
    const int = formatNumber(parseEuroNumber(commission), 'en-US');
    const dec = String(parseInt(cents || '0', 10)).padStart(2, '0');
    return `$${int}.${dec}`;
  };

  const handleFormSubmit = () => {
    setSubmitConfirmOpen(true);
  };

  const confirmSubmit = (data) => {
    onSubmitCommission(data);
  };

  const handleClose = () => {
    setOpenDialog(false);
    reset();
  };

  const handleDialogClose = (_event, reason) => {
    // Firefox fires a backdrop click when opening a Select near the bottom of
    // the dialog (Event Type). Chrome does not. Keep Cancel / Escape.
    if (reason === 'backdropClick') {
      return;
    }
    handleClose();
  };

  const isErrorOtherEvent = selectedEventType === 'Other' && !otherEvent && touchedFields?.otherEvent;

  return (
    <>
      <Dialog open={openDialog} onClose={handleDialogClose} disableRestoreFocus fullWidth maxWidth='sm'>
        <DialogTitle>
          Submit Commission for {currentMonth} {currentYear}
        </DialogTitle>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogContent dividers>
              {isAdmin && <TextField label='Lead Agent' name='leadAgent' disabled />}
              <SelectField label='Client' name='client' options={companiesList} disabled={isEdit} />
              <SelectField
                label='Product'
                name='product'
                options={productsList}
                disabled={isEdit || !selectedClientId || productsList.length === 0}
              />
              {!isEdit && selectedClientId && productsList.length === 0 ? (
                <Typography variant='caption' color='error' display='block' mb={1}>
                  This client has no products. A product is required to submit a commission.
                </Typography>
              ) : null}
              <TextField label='Prospect' name='prospect' />
              <SelectField label='Event Type' name='event' options={eventTypesList} />
              {selectedEventType === 'Other' && (
                <TextField
                  label='Custom Event Description'
                  name='otherEvent'
                  error={isErrorOtherEvent}
                  helperText={isErrorOtherEvent && 'This field is required.'}
                />
              )}

              <Box display='flex' gap={2} alignItems='start'>
                <EuroNumberInput label='Commission' name='commission' />
                <TextField label='Cents' name='cents' onChange={handleDecimalChange} />
              </Box>

              <Typography variant='caption' display='block'>
                US Format: {convertToUSDFormat()}
              </Typography>

              <TextField label='Notes (optional)' name='notes' />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color='secondary'>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || !isDirty || (selectedEventType === 'Other' && !otherEvent)}
              >
                Submit
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>

      <Dialog open={submitConfirmOpen} onClose={() => setSubmitConfirmOpen(false)}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          By clicking continue, this commission request will be forwarded to <strong>{selectedClientName}</strong> for
          approval.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitConfirmOpen(false)} color='secondary' disabled={isSubmitting}>
            Go Back
          </Button>
          <Button onClick={handleSubmit(confirmSubmit)} variant='contained' color='primary' disabled={isSubmitting}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubmitCommissionDialog;
